import React, { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, ArrowUp, ArrowDown, Search, X, Filter } from 'lucide-react';
import { Column, TableProps, BulkAction } from './types';

const CustomTable: React.FC<TableProps> = ({
  columns,
  data,
  keyField = 'id',
  sortable = true,
  bulkActions = [],
  selectable = true,
  onSelectionChange,
  pagination = false,
  itemsPerPage = 10,
  searchable = false,
  searchFields = [],
  searchPlaceholder = "Search...",
  className = '',
  
  // Server-side pagination
  currentPage: externalCurrentPage,
  totalPages: externalTotalPages,
  totalItems: externalTotalItems,
  onPageChange,
  
  // Loading state
  loading = false
}) => {
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
  const [allSelected, setAllSelected] = useState(false);
  const [internalCurrentPage, setInternalCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  // Determine if using server-side pagination
  const isServerSidePagination = externalCurrentPage !== undefined;
  
  // Use external or internal pagination state
  const currentPage = isServerSidePagination ? externalCurrentPage! : internalCurrentPage;
  const totalItems = isServerSidePagination ? externalTotalItems! : data.length;
  const totalPages = isServerSidePagination ? externalTotalPages! : Math.ceil(totalItems / itemsPerPage);

  // Reset selection when data changes or page changes
  useEffect(() => {
    setSelectedRows([]);
    setAllSelected(false);
  }, [data, currentPage]);

  // Notify parent of selection changes
  useEffect(() => {
    onSelectionChange?.(selectedRows);
  }, [selectedRows, onSelectionChange]);

  // Get searchable field labels for display
  const searchableFieldLabels = useMemo(() => {
    if (searchFields.length === 0) return [];
    
    return searchFields.map(field => {
      const column = columns.find(col => col.key === field);
      return column?.label || field;
    });
  }, [searchFields, columns]);

  // Filter data based on search term in specified fields (client-side only)
  const filteredData = useMemo(() => {
    if (!searchTerm.trim() || isServerSidePagination) return data;

    const lowerSearchTerm = searchTerm.toLowerCase();
    const fieldsToSearch = searchFields.length > 0 ? searchFields : columns.map(col => col.key);
    
    return data.filter(row => {
      return fieldsToSearch.some(field => {
        const value = row[field];
        return value?.toString().toLowerCase().includes(lowerSearchTerm);
      });
    });
  }, [data, searchTerm, searchFields, columns, isServerSidePagination]);

  // Sort data based on sort configuration (client-side only)
  const sortedData = useMemo(() => {
    if (!sortConfig || isServerSidePagination) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortConfig.key];
      const bValue = b[sortConfig.key];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }, [filteredData, sortConfig, isServerSidePagination]);

  // Paginate data (client-side only)
  const paginatedData = useMemo(() => {
    if (!pagination || isServerSidePagination) return sortedData;

    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage, pagination, itemsPerPage, isServerSidePagination]);

  // Calculate display data
  const displayData = isServerSidePagination ? data : paginatedData;

  // Calculate pagination info for display
  const clientSideTotalItems = isServerSidePagination ? totalItems : filteredData.length;
  const startItem = pagination ? (currentPage - 1) * itemsPerPage + 1 : 1;
  const endItem = pagination 
    ? Math.min(currentPage * itemsPerPage, clientSideTotalItems)
    : clientSideTotalItems;

  const handleSort = (key: string) => {
    if (!sortable || isServerSidePagination) return;

    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key) {
      direction = sortConfig.direction === 'asc' ? 'desc' : 'asc';
    }

    setSortConfig({ key, direction });
    if (!isServerSidePagination) {
      setInternalCurrentPage(1);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = displayData.map((row: any) => row[keyField]);
      setSelectedRows(allIds);
      setAllSelected(true);
    } else {
      setSelectedRows([]);
      setAllSelected(false);
    }
  };

  const handleSelectRow = (id: string | number) => {
    setSelectedRows(prev => {
      const newSelection = prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id];
      
      setAllSelected(newSelection.length === displayData.length);
      return newSelection;
    });
  };

  const getSortIcon = (columnKey: string) => {
    if (!sortConfig || sortConfig.key !== columnKey) {
      return <ArrowUp className="size-3 opacity-30" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="size-3" /> 
      : <ArrowDown className="size-3" />;
  };

  const renderCell = (column: Column, row: any) => {
    if (column.render) {
      return column.render(row[column.key], row);
    }
    return row[column.key];
  };

  const handlePageChange = (page: number) => {
    if (isServerSidePagination) {
      onPageChange?.(page);
    } else {
      setInternalCurrentPage(page);
    }
    setSelectedRows([]);
    setAllSelected(false);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    if (!isServerSidePagination) {
      setInternalCurrentPage(1);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    if (!isServerSidePagination) {
      setInternalCurrentPage(1);
    }
  };

  // Skeleton loading rows
  const renderSkeletonRows = () => {
    const skeletonRows = [];
    const rowsCount = pagination ? itemsPerPage : 5; // Show 5 skeleton rows if no pagination
    
    for (let i = 0; i < rowsCount; i++) {
      skeletonRows.push(
        <tr 
          key={`skeleton-${i}`} 
          className={`border-b border-slate-100 dark:border-zink-500 ${
            i % 2 === 0 
              ? 'bg-white dark:bg-zink-700' 
              : 'bg-slate-50/30 dark:bg-zink-600/30'
          }`}
        >
          {/* Select Checkbox Skeleton */}
          {selectable && (
            <td className="px-4 py-3">
              <div className="flex items-center">
                <div className="size-4 bg-slate-200 dark:bg-zink-500 rounded animate-pulse"></div>
              </div>
            </td>
          )}
          
          {/* Column Skeleton Cells */}
          {columns.map((column, colIndex) => (
            <td key={colIndex} className="px-4 py-3">
              <div className="flex items-center space-x-2">
                <div 
                  className={`bg-slate-200 dark:bg-zink-500 rounded animate-pulse ${
                    colIndex === 0 ? 'h-4 w-32' : 
                    colIndex === columns.length - 1 ? 'h-8 w-20' : 
                    'h-4 w-24'
                  }`}
                ></div>
              </div>
            </td>
          ))}
        </tr>
      );
    }
    
    return skeletonRows;
  };

  const renderPaginationButtons = () => {
    const buttons = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    if (startPage > 1) {
      buttons.push(
        <li key={1}>
          <button
            onClick={() => handlePageChange(1)}
            className="inline-flex items-center justify-center bg-white dark:bg-zink-700 size-8 transition-all duration-150 ease-linear border border-slate-200 dark:border-zink-500 rounded text-slate-500 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-50 dark:hover:bg-custom-500/10 focus:bg-custom-50 dark:focus:bg-custom-500/10 focus:text-custom-500 dark:focus:text-custom-500"
          >
            1
          </button>
        </li>
      );
      if (startPage > 2) {
        buttons.push(
          <li key="ellipsis1">
            <span className="inline-flex items-center justify-center size-8 text-slate-400 dark:text-zink-300">...</span>
          </li>
        );
      }
    }

    for (let page = startPage; page <= endPage; page++) {
      buttons.push(
        <li key={page}>
          <button
            onClick={() => handlePageChange(page)}
            className={`inline-flex items-center justify-center bg-white dark:bg-zink-700 size-8 transition-all duration-150 ease-linear border border-slate-200 dark:border-zink-500 rounded text-slate-500 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-50 dark:hover:bg-custom-500/10 focus:bg-custom-50 dark:focus:bg-custom-500/10 focus:text-custom-500 dark:focus:text-custom-500 ${
              page === currentPage
                ? 'text-custom-50 dark:text-custom-50 bg-custom-500 dark:bg-custom-500 border-custom-500 dark:border-custom-500'
                : ''
            }`}
          >
            {page}
          </button>
        </li>
      );
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        buttons.push(
          <li key="ellipsis2">
            <span className="inline-flex items-center justify-center size-8 text-slate-400 dark:text-zink-300">...</span>
          </li>
        );
      }
      buttons.push(
        <li key={totalPages}>
          <button
            onClick={() => handlePageChange(totalPages)}
            className="inline-flex items-center justify-center bg-white dark:bg-zink-700 size-8 transition-all duration-150 ease-linear border border-slate-200 dark:border-zink-500 rounded text-slate-500 dark:text-zink-200 hover:text-custom-500 dark:hover:text-custom-500 hover:bg-custom-50 dark:hover:bg-custom-500/10 focus:bg-custom-50 dark:focus:bg-custom-500/10 focus:text-custom-500 dark:focus:text-custom-500"
          >
            {totalPages}
          </button>
        </li>
      );
    }

    return buttons;
  };

  return (
    <div className={className}>
      {/* Search Bar - Modern Compact Design */}
      {searchable && (
        <div className="flex items-center justify-between mb-6 p-4 bg-white dark:bg-zink-700 rounded-lg border border-slate-200 dark:border-zink-600 shadow-sm">
          <div className="flex items-center gap-4 flex-1 max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 size-4 transition-colors" />
              <input
                type="text"
                value={searchTerm}
                onChange={handleSearchChange}
                placeholder={searchPlaceholder}
                className="w-full pl-10 pr-10 py-2.5 border border-slate-200 dark:border-zink-500 rounded-lg bg-white dark:bg-zink-700 text-slate-600 dark:text-zink-200 placeholder-slate-400 dark:placeholder-zink-300 focus:outline-none focus:ring-2 focus:ring-custom-500/20 focus:border-custom-500 dark:focus:border-custom-500 transition-all duration-200 text-sm"
              />
              {searchTerm && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-zink-300 transition-colors duration-200"
                >
                  <X className="size-4" />
                </button>
              )}
            </div>
            
            {/* Search Info Badge */}
            {searchTerm && !isServerSidePagination && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-custom-50 dark:bg-custom-500/10 border border-custom-200 dark:border-custom-500/20 rounded-lg">
                <span className="text-xs font-medium text-custom-600 dark:text-custom-400">
                  {filteredData.length}/{data.length}
                </span>
              </div>
            )}
          </div>

          {/* Search Fields Info - Only show when specific fields are defined */}
          {searchFields.length > 0 && (
            <div className="hidden lg:flex items-center gap-2 text-sm text-slate-500 dark:text-zink-400">
              <Filter className="size-4" />
              <span className="whitespace-nowrap">Searching in:</span>
              <div className="flex gap-1">
                {searchableFieldLabels.slice(0, 2).map((label, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-slate-100 dark:bg-zink-600 rounded text-xs font-medium text-slate-600 dark:text-zink-300 whitespace-nowrap"
                  >
                    {label}
                  </span>
                ))}
                {searchableFieldLabels.length > 2 && (
                  <span className="px-2 py-1 bg-slate-100 dark:bg-zink-600 rounded text-xs font-medium text-slate-600 dark:text-zink-300">
                    +{searchableFieldLabels.length - 2} more
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Bulk Actions - Modern Design */}
      {selectable && bulkActions.length > 0 && selectedRows.length > 0 && (
        <div className="flex items-center justify-between mb-4 p-3 bg-gradient-to-r from-custom-50 to-blue-50 dark:from-custom-500/10 dark:to-blue-500/10 rounded-lg border border-custom-200 dark:border-custom-500/20">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center size-8 bg-custom-500 rounded-lg shadow-sm">
              <span className="text-white text-sm font-semibold">{selectedRows.length}</span>
            </div>
            <div>
              <p className="text-custom-700 dark:text-custom-300 font-medium text-sm">
                {selectedRows.length} item{selectedRows.length > 1 ? 's' : ''} selected
              </p>
              <p className="text-custom-600 dark:text-custom-400 text-xs">
                Choose an action below
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            {bulkActions.map((action: BulkAction, index: number) => (
              <button
                key={index}
                onClick={() => action.action(selectedRows)}
                className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-all duration-200 ease-linear ${
                  action.variant === 'danger'
                    ? 'text-white bg-gradient-to-r from-red-500 to-red-600 border-red-500 hover:from-red-600 hover:to-red-700 shadow-sm hover:shadow'
                    : action.variant === 'secondary'
                    ? 'text-slate-700 dark:text-zink-200 bg-white dark:bg-zink-600 border-slate-200 dark:border-zink-500 hover:bg-slate-50 dark:hover:bg-zink-500 shadow-sm hover:shadow'
                    : 'text-white bg-gradient-to-r from-custom-500 to-custom-600 border-custom-500 hover:from-custom-600 hover:to-custom-700 shadow-sm hover:shadow'
                }`}
              >
                {action.icon && <span className="scale-110">{action.icon}</span>}
                {action.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Table - Clean Modern Design */}
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-zink-500 bg-white dark:bg-zink-700 shadow-sm">
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50 dark:bg-zink-600 border-b border-slate-200 dark:border-zink-500">
              {/* Select All Checkbox */}
              {selectable && (
                <th className="px-4 py-3 first:rounded-tl-lg">
                  {loading ? (
                    <div className="size-4 bg-slate-200 dark:bg-zink-500 rounded animate-pulse"></div>
                  ) : (
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={handleSelectAll}
                        className="size-4 bg-white border border-slate-300 checked:bg-none dark:bg-zink-700 dark:border-zink-500 rounded transition-all duration-200 relative after:absolute after:content-['\eb7b'] after:top-0 after:left-0 after:font-remix after:leading-none after:opacity-0 checked:after:opacity-100 after:text-white checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-500 dark:checked:border-custom-500"
                      />
                    </div>
                  )}
                </th>
              )}

              {/* Column Headers */}
              {columns.map((column: Column) => (
                <th
                  key={column.key}
                  className={`px-4 py-3 text-left ${
                    column.sortable && !isServerSidePagination ? 'cursor-pointer select-none hover:bg-slate-100 dark:hover:bg-zink-500 transition-colors duration-150' : ''
                  }`}
                  onClick={() => column.sortable && !loading && handleSort(column.key)}
                >
                  <div className="flex items-center gap-2 group">
                    {loading ? (
                      <div className="h-4 bg-slate-200 dark:bg-zink-500 rounded animate-pulse w-24"></div>
                    ) : (
                      <>
                        <span className="text-sm font-semibold text-slate-700 dark:text-zink-200 uppercase tracking-wide">
                          {column.label}
                        </span>
                        {column.sortable && !isServerSidePagination && (
                          <div className="flex flex-col -space-y-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            {getSortIcon(column.key)}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading ? (
              // Skeleton Loading Rows
              renderSkeletonRows()
            ) : (
              // Actual Data Rows
              <>
                {displayData.map((row: any, rowIndex) => (
                  <tr 
                    key={row[keyField]} 
                    className={`border-b border-slate-100 dark:border-zink-500 transition-colors duration-150 ${
                      rowIndex % 2 === 0 
                        ? 'bg-white dark:bg-zink-700' 
                        : 'bg-slate-50/30 dark:bg-zink-600/30'
                    } hover:bg-slate-100 dark:hover:bg-zink-600`}
                  >
                    {/* Row Checkbox */}
                    {selectable && (
                      <td className="px-4 py-3">
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={selectedRows.includes(row[keyField])}
                            onChange={() => handleSelectRow(row[keyField])}
                            className="size-4 bg-white border border-slate-300 checked:bg-none dark:bg-zink-700 dark:border-zink-500 rounded transition-all duration-200 relative after:absolute after:content-['\eb7b'] after:top-0 after:left-0 after:font-remix after:leading-none after:opacity-0 checked:after:opacity-100 after:text-white checked:bg-custom-500 checked:border-custom-500 dark:checked:bg-custom-500 dark:checked:border-custom-500"
                          />
                        </div>
                      </td>
                    )}

                    {/* Data Cells */}
                    {columns.map((column: Column) => (
                      <td
                        key={column.key}
                        className={`px-4 py-3 text-sm text-slate-600 dark:text-zink-200 ${column.className || ''}`}
                      >
                        {renderCell(column, row)}
                      </td>
                    ))}
                  </tr>
                ))}

                {/* Empty State */}
                {displayData.length === 0 && (
                  <tr>
                    <td 
                      colSpan={columns.length + (selectable ? 1 : 0)} 
                      className="px-4 py-12 text-center"
                    >
                      <div className="flex flex-col items-center gap-3 max-w-sm mx-auto">
                        <div className="size-16 bg-slate-100 dark:bg-zink-600 rounded-full flex items-center justify-center">
                          {searchTerm ? (
                            <Search className="size-6 text-slate-400" />
                          ) : (
                            <div className="size-6 bg-slate-300 dark:bg-zink-500 rounded" />
                          )}
                        </div>
                        <div className="text-center">
                          <p className="font-semibold text-slate-700 dark:text-zink-300 text-base mb-1">
                            {searchTerm ? 'No results found' : 'No data available'}
                          </p>
                          <p className="text-slate-500 dark:text-zink-400 text-sm">
                            {searchTerm 
                              ? 'Try adjusting your search to find what you\'re looking for' 
                              : 'Get started by adding your first record'
                            }
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination - Modern Compact Design */}
      {pagination && totalPages > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 p-4 bg-white dark:bg-zink-700 rounded-lg border border-slate-200 dark:border-zink-500">
          {/* Results Info */}
          <div className="text-sm text-slate-600 dark:text-zink-300">
            {loading ? (
              <div className="flex items-center space-x-2">
                <div className="h-4 bg-slate-200 dark:bg-zink-500 rounded animate-pulse w-20"></div>
                <div className="h-4 bg-slate-200 dark:bg-zink-500 rounded animate-pulse w-16"></div>
                <div className="h-4 bg-slate-200 dark:bg-zink-500 rounded animate-pulse w-24"></div>
              </div>
            ) : (
              <>
                <span className="font-medium text-slate-700 dark:text-zink-200">{startItem}-{endItem}</span>
                <span className="mx-1">of</span>
                <span className="font-medium text-slate-700 dark:text-zink-200">{totalItems}</span>
                <span className="text-slate-500 dark:text-zink-400"> results</span>
                {searchTerm && !isServerSidePagination && (
                  <span className="text-slate-400 dark:text-zink-500 text-xs ml-2">
                    (filtered from {data.length})
                  </span>
                )}
              </>
            )}
          </div>

          {/* Pagination Controls */}
          {!loading && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="inline-flex items-center justify-center size-8 transition-all duration-200 ease-linear border border-slate-200 dark:border-zink-500 rounded text-slate-500 dark:text-zink-300 hover:text-white dark:hover:text-white hover:bg-custom-500 hover:border-custom-500 disabled:text-slate-300 dark:disabled:text-zink-500 disabled:cursor-not-allowed disabled:bg-slate-50 dark:disabled:bg-zink-600"
              >
                <ChevronLeft className="size-4 rtl:rotate-180" />
              </button>

              {/* Page Numbers */}
              <div className="flex items-center gap-1 mx-2">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page)}
                      className={`inline-flex items-center justify-center size-8 text-sm font-medium transition-all duration-200 ease-linear border rounded ${
                        page === currentPage
                          ? 'text-white bg-custom-500 border-custom-500 shadow-sm'
                          : 'text-slate-600 dark:text-zink-300 border-slate-200 dark:border-zink-500 hover:text-custom-500 dark:hover:text-custom-500 hover:border-custom-500 dark:hover:border-custom-500'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                {totalPages > 5 && (
                  <span className="px-2 text-slate-400 dark:text-zink-500 text-sm">...</span>
                )}
              </div>

              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="inline-flex items-center justify-center size-8 transition-all duration-200 ease-linear border border-slate-200 dark:border-zink-500 rounded text-slate-500 dark:text-zink-300 hover:text-white dark:hover:text-white hover:bg-custom-500 hover:border-custom-500 disabled:text-slate-300 dark:disabled:text-zink-500 disabled:cursor-not-allowed disabled:bg-slate-50 dark:disabled:bg-zink-600"
              >
                <ChevronRight className="size-4 rtl:rotate-180" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomTable;